import { IconTypes, InvestorBadgeTypes } from '@genial/components';
import { ColorTypes } from '@genial/design-tokens';
import { IconographyTypes } from '@genial/icons';
import {
  BackendError,
  FinancialCustomerProfitabilityChartDatapointEntity,
  Service,
} from '@genial/services';
import { MetaResponse } from '@genial/services/dist/models';
import { Utils } from '@genial/utils';
import { selector, selectorFamily } from 'recoil';

import { blankMetaResponse } from '../../utils/blankMetaResponse';
import { mountAccount } from '../customer/normalize';
import {
  LiquidityAssetsQueryItem,
  RecommendedPortfolioResponse,
  StrategyAssetsQueryItem,
} from '../customerAssetDetail/types';
import { advisorIdToApisSelector, partnerIdToApisSelector } from '../master/selector';

import { customersAssetsDateSelected, isExportingPdfForCustomersAssets } from './atom';
import { screenName } from './constants';
import {
  normalizeCustomerAssets,
  normalizeCustomerBlocked,
  normalizeCustomerBlockingReasons,
  normalizeCustomerContractedProducts,
  normalizeCustomerDetailFullAccountData,
  normalizeCustomerGenialLimit,
  normalizeCustomerStrategyOrLiquidityDetails,
  normalizeCustomerTransits,
  normalizeRecommendedPortfolioDetails,
  orderAdditionalAccounts,
} from './normalize';
import {
  CustomerDetailFullAccount,
  CustomerFinancialPersonalDataResponseNormalized,
  CustomerSentOrdersResponseNormalized,
  FinancialCustomerCompositionChartResponseNormalized,
  FinancialCustomerPortfolioProfitabilityResponseNormalized,
  FinancialCustomerProfitabilityChartAssetsDatapointsResponseNormalized,
  FinancialCustomerProfitabilityChartAssetsResponseNormalized,
  FinancialCustomerProfitabilityChartDatapointEntityNormalized,
  ICustomerContractedProductsNormalized,
  ICustomerBlockedNormalized,
  ICustomerInvestedAssetsNormalized,
  ICustomerTransitNormalized,
  ICustomerGenialLimitNormalized,
} from './types';

export const customerDetailQuery = selectorFamily<
  CustomerFinancialPersonalDataResponseNormalized | null | undefined,
  { customerId: number; accountId: number }
>({
  key: 'CustomerDetail/Query',
  get:
    ({ customerId, accountId }) =>
    async ({ get }) => {
      const advisorSelected = get(advisorIdToApisSelector);
      const partnerSelected = get(partnerIdToApisSelector);

      const { data } = await Service.Customer.V2.getFinancialByPersonalDataCustomerIdAccountId({
        customerId,
        accountId,
        screenName,
        advisorId: advisorSelected,
        partnerId: partnerSelected,
      });

      if (!data) {
        return data;
      }

      let investorType: InvestorBadgeTypes.InvestorType = InvestorBadgeTypes.InvestorType.Common;

      if (data.qualifiedInvestor) {
        investorType = InvestorBadgeTypes.InvestorType.Qualified;
      } else if (data.professionalInvestor) {
        investorType = InvestorBadgeTypes.InvestorType.Professional;
      }

      const normalizedCustomer: CustomerFinancialPersonalDataResponseNormalized = {
        ...data,
        customerEmailNormalized: data.email ?? '--',
        customerNameNormalized: data.fullName ?? '--',
        initialsNormalized: `${data.firstName?.charAt(0) ?? ''}${data.lastName?.charAt(0) ?? ''}`,
        availableBalanceNormalized: Utils.Mask.Money(data.availableBalance),
        balanceD1Normalized: Utils.Mask.Money(data.availableBalanceD1),
        balanceD2Normalized: Utils.Mask.Money(data.availableBalanceD2),
        suitabilityColorNormalized: Utils.Suitability.getCorrectColor(
          data.suitabilityProfileName || ''
        ),
        suitabilityProfileAcronymNormalized: data.suitabilityProfileAcronym ?? '--',
        suitabilityDueDateFormattedNormalized:
          data.suitabilityProfileExpirationDateFormatted ?? '--',
        suitabilityProfileNameNormalized: data.suitabilityProfileName ?? '--',
        personalNormalized: {
          birthDateNormalized: data.birthDayFormatted ?? '--',
          cityNameNormalized: data.cityFormatted ?? '--',
          cpfCnpjNormalized: data.cpfCnpjFormatted ?? '--',
          phoneNumberNormalized:
            data.phone?.length ?? 0 >= 10
              ? `(${data.phone?.substring(0, 2)}) ${data.phone?.substring(2)}`
              : `(${data.phoneAreaCode || '--'}) ${data.phone || '--'}`,
          stateNameNormalized: data.state ?? '--',
          stateAcronymNormalized: data.stateAcrom ?? '--',
          zipCodeNormalized: data.zipCode ?? '--',
          addressNormalized: data.address ?? '--',
          addressNumberNormalized: data.addressNumber ?? '--',
          neighborhoodNormalized: data.neighborhood ?? '--',
          maritalStatusNormalized: data.maritalStatus ? data.maritalStatus : '--',
          genderNormalized: data.genderFormatted ?? '--',
          professionNormalized: data.profession ?? '--',
        },
        accountNormalized: {
          accountNumberNormalized: `${data.accountNumber}-${data.accountNumberDigit}`,
          accountStatusNormalized: data.status?.statusName?.toUpperCase() ?? '--',
          accountStatusColorNormalized: Utils.ColorToken.fromColor(data.status?.iconColor),
          accountStatusIconColorNormalized: Utils.ColorToken.fromColor(data.status?.iconColor),
          accountStatusIconNormalized: (data.status?.icon ?? 'Info') as IconographyTypes.Option,
          accountStatusIconSizeNormalized:
            (data.status?.iconSize as IconTypes.Props['size']) ?? 'xxs',
          accountDateNormalized: data.createdAt
            ? Utils.Mask.DateFormat(data.createdAt, 'dd/MM/yyyy')
            : '--',
          sinacorAccountNormalized: `${data.accountNumber}-${data.accountNumberDigit}`,
          digitalAccountNormalized: `${data.digitalAccountNumber}-${data.digitalAccountDigit}`,
          rlpActiveNormalized: data.rlpActivated ? 'SIM' : 'NÃO',
          intradayLeverageNormalized: data.intradayLeverage ? 'CONTRATADO' : 'NÃO CONTRATADO',
        },
        investorTypeNormalized: investorType,
      };

      return normalizedCustomer;
    },
});

export const groupedCustomerDetailQuery = selectorFamily<
  CustomerDetailFullAccount | null | undefined,
  { accountNumber: number }
>({
  key: 'groupedCustomerDetailQuery',
  get:
    ({ accountNumber }) =>
    async ({ get }) => {
      const advisorSelected = get(advisorIdToApisSelector);
      const partnerSelected = get(partnerIdToApisSelector);

      const { data } = await Service.Customer.V3.getFinancialData({
        accountNumber,
        screenName,
        advisorId: advisorSelected,
        partnerId: partnerSelected,
      });

      const normalizedCustomer = normalizeCustomerDetailFullAccountData(data);
      const orderedAndNormalizedCustomer = orderAdditionalAccounts(
        normalizedCustomer,
        accountNumber
      );

      return orderedAndNormalizedCustomer;
    },
});

export const customerDetailFullAccountDataQuery = selectorFamily<
  CustomerDetailFullAccount,
  { accountNumber: number }
>({
  key: 'CustomerDetail/AccountDataQuery',
  get:
    ({ accountNumber }) =>
    async ({ get }) => {
      const advisorSelected = get(advisorIdToApisSelector);
      const partnerSelected = get(partnerIdToApisSelector);

      const { data } = await Service.Customer.V3.getFinancialData({
        accountNumber,
        screenName,
        advisorId: advisorSelected,
        partnerId: partnerSelected,
      });

      const normalizedData = normalizeCustomerDetailFullAccountData(data);

      return normalizedData;
    },
});

export const customerDetailPortfolioProfitabilityQuery = selectorFamily<
  FinancialCustomerPortfolioProfitabilityResponseNormalized | null | undefined,
  { accountId: number }
>({
  key: 'Customer/Detail/ProfitabilityQuery',
  get:
    ({ accountId }) =>
    async () => {
      const { data } = await Service.Customer.V1.getFinancialByPortfolioProfitabilityAccountId({
        accountId,
        screenName,
      });

      const normalized = data
        ? {
            ...data,
            yearValueNormalized: isNaN(data?.yearValue) ? '--' : Utils.Mask.Money(data.yearValue),
            yearValuePercentageNormalized: isNaN(data?.yearValuePercentage)
              ? '--'
              : Utils.Mask.Percentage(data.yearValuePercentage),
            yearValuePercentageColorNormalized: Utils.ColorToken.fromNumber(
              data.yearValuePercentage
            ),
            monthValueNormalized: isNaN(data?.monthValue)
              ? '--'
              : Utils.Mask.Money(data.monthValue),
            monthValuePercentageNormalized: isNaN(data?.monthValuePercentage)
              ? '--'
              : Utils.Mask.Percentage(data.monthValuePercentage),
            monthValuePercentageColorNormalized: Utils.ColorToken.fromNumber(
              data.monthValuePercentage
            ),
            twelveMonthsValueNormalized: isNaN(data?.twelveMonthsValue)
              ? '--'
              : Utils.Mask.Money(data.twelveMonthsValue),
            twelveMonthsValuePercentageNormalized: isNaN(data?.twelveMonthsValuePercentage)
              ? '--'
              : Utils.Mask.Percentage(data.twelveMonthsValuePercentage),
            twelveMonthsValuePercentageColorNormalized: Utils.ColorToken.fromNumber(
              data.twelveMonthsValuePercentage
            ),
            allTimeValueNormalized: isNaN(data?.allTimeValue)
              ? '--'
              : Utils.Mask.Money(data.allTimeValue),
            allTimeValuePercentageNormalized: isNaN(data?.allTimeValuePercentage)
              ? '--'
              : Utils.Mask.Percentage(data.allTimeValuePercentage),
            allTimeValuePercentageColorNormalized: Utils.ColorToken.fromNumber(
              data.allTimeValuePercentage
            ),
          }
        : data;

      return normalized;
    },
});

export const customerDetailCompositionPortfolioQuery = selectorFamily<
  FinancialCustomerCompositionChartResponseNormalized | null | undefined,
  { accountNumber: number }
>({
  key: 'Customer/Detail/CompositionPortfolioQuery',
  get:
    ({ accountNumber }) =>
    async () => {
      const { data } = await Service.Finance.V1.getCompositionByPortfolioAccountNumber({
        accountNumber,
        screenName,
      });

      if (!data) {
        return data;
      }

      // Users has nothing on his portfolio
      if (!Object.keys(data).length) {
        return {
          composition: [],
          assets: [],
          blocking: [],
          strategy: [],
          liquidity: [],
          total: { totalBlockingAssets: 0, totalInvested: 0, totalNetWorth: 0 },
        } as any;
      }

      const normalized: FinancialCustomerCompositionChartResponseNormalized = Object.entries(data)
        .map(([key, value]) => {
          if (key === 'blocking') {
            return {
              blocking: [
                {
                  productType: 'blockingAssets',
                  nameNormalized: 'Patrimônio',
                  percentage: value.blockedAssetsPercentage ?? 0,
                  percentageNormalized: Utils.Mask.Percentage(value?.blockedAssetsPercentage ?? 0),
                  value: value.blockedAssets ?? 0,
                  valueNormalized: Utils.Mask.Percentage(value?.blockedAssetsPercentage ?? 0),
                  colorNormalized: value?.blockedAssetColor ?? 'background',
                },
                {
                  productType: 'blockingValue',
                  nameNormalized: 'Saldo bloqueado',
                  percentage: value.blockedValuePercentage,
                  percentageNormalized: Utils.Mask.Percentage(value?.blockedValuePercentage),
                  value: value.blockedValuePercentage,
                  valueNormalized: Utils.Mask.Percentage(value?.blockedValuePercentage),
                  colorNormalized: value?.blockedColor ?? 'background',
                },
              ],
            };
          } else if (key === 'total') {
            return {
              [key]: value,
            };
          } else if (key === 'strategy') {
            return {
              [key]:
                value.map((strategy) => ({
                  productType: strategy.strategyType,
                  nameNormalized: strategy.description,
                  percentage: strategy.percentage,
                  percentageNormalized: Utils.Mask.Percentage(strategy.percentage),
                  value: strategy.value,
                  valueNormalized: Utils.Mask.Money(strategy.value),
                  colorNormalized:
                    (strategy.strategyTypeColor as ColorTypes.Option) ?? 'background',
                })) ?? [],
            };
          } else if (key === 'liquidity') {
            return {
              [key]:
                value.map((liquidity) => ({
                  productType: liquidity.liquidity,
                  nameNormalized: liquidity.description,
                  percentage: liquidity.percentage,
                  percentageNormalized: Utils.Mask.Percentage(liquidity.percentage),
                  value: liquidity.value,
                  valueNormalized: Utils.Mask.Money(liquidity.value),
                  colorNormalized: (liquidity.liquidityColor as ColorTypes.Option) ?? 'background',
                })) ?? [],
            };
          } else {
            return {
              [key]:
                value?.map((composition) => ({
                  productType: composition.marketType || composition.assetType,
                  nameNormalized: (composition.marketTypeName || composition.assetTypeName) ?? '--',
                  percentage: composition.percentage,
                  percentageNormalized: Utils.Mask.Percentage(composition.percentage),
                  value: composition.value,
                  valueNormalized: Utils.Mask.Money(composition.value),
                  colorNormalized:
                    ((composition.marketTypeProductColor ||
                      composition.assetTypeProductColor) as ColorTypes.Option) ?? 'background',
                })) ?? [],
            };
          }
        })
        .flatMap((item) => item)
        .reduce((acc, item) => ({ ...acc, ...item }), {});

      if (normalized.composition) {
        normalized.composition = normalized.composition.filter((asset) => asset.value !== 0);
      }

      if (normalized.liquidity) {
        normalized.liquidity = normalized.liquidity.filter((asset) => asset.value !== 0);
      }
      if (normalized.strategy) {
        normalized.strategy = normalized.strategy.filter((asset) => asset.value !== 0);
      }

      return normalized;
    },
});

export const customerDetailProfitabilityChartAssetsQuery = selector<
  FinancialCustomerProfitabilityChartAssetsResponseNormalized | null | undefined
>({
  key: 'Customer/Detail/ProfitabilityChartAssetsQuery',
  get: async () => {
    const { data } = await Service.Customer.V1.getFinancialProfitabilityChartAssetsTypes({
      screenName,
    });

    const normalized = data
      ? data.map((item) => {
          return {
            assetTypeDescriptionNormalized: item.assetTypeDescription?.toUpperCase() ?? '--',
            assetType: item.assetType,
            assetTypeId: item.assetTypeId,
          };
        })
      : data;

    return normalized;
  },
});

export const customerDetailProfitabilityChartAssetsDatapointsQuery = selectorFamily<
  FinancialCustomerProfitabilityChartAssetsDatapointsResponseNormalized | null | undefined,
  { accountId: number; assetTypeId: number }
>({
  key: 'Customer/Detail/ProfitabilityChartAssetsDatapointsQuery',
  get:
    ({ accountId, assetTypeId }) =>
    async () => {
      if (assetTypeId === -1) {
        return null;
      }

      const { data } = await Service.Customer.V1.getFinancialProfitabilityChartAccountIdAssetType({
        accountId,
        assetTypeId,
        screenName,
      });

      if (!data) {
        return data;
      }

      const mapDatapoints = (
        entries:
          | {
              [key: string]: FinancialCustomerProfitabilityChartDatapointEntity[] | undefined;
            }
          | null
          | undefined
      ): { [k: string]: FinancialCustomerProfitabilityChartDatapointEntityNormalized[] } => {
        if (!entries) {
          return {};
        }

        return Object.entries(entries).reduce(
          (acc, [key, value]) => ({
            ...acc,
            [key]: value?.map((datapoint) => ({
              ...datapoint,
              period: new Date(datapoint.period ?? 0),
              periodNormalized: Utils.Mask.DateFormat(datapoint.period!, 'dd/MM/yyyy'),
              monthNormalized: datapoint.month ?? '--',
              fullMonthNormalized: Utils.Mask.DateFormat(datapoint.period!, 'MMMM'),
            })),
          }),
          {}
        );
      };

      const normalized: FinancialCustomerProfitabilityChartAssetsDatapointsResponseNormalized = {
        periodDatapoints:
          data.datapoints
            ?.map((block) => mapDatapoints(block?.periodDatapoints))
            .reduce((acc, item) => ({ ...acc, ...item }), {}) ?? ({} as any),
        allTimeDatapoints:
          data.datapoints
            ?.map((block) => mapDatapoints(block?.allTimeDatapoints))
            .reduce((acc, item) => ({ ...acc, ...item }), {}) ?? ({} as any),
      };

      return normalized;
    },
});

export const customerDetailSentOrdersQuery = selectorFamily<
  {
    data: CustomerSentOrdersResponseNormalized[] | null | undefined;
    meta: MetaResponse;
  },
  { customerId: number; page: number }
>({
  key: 'Customer/Detail/SentOrdersQuery',
  get:
    ({ customerId, page }) =>
    async ({ get }) => {
      try {
        const advisorSelected = get(advisorIdToApisSelector);
        const partnerSelected = get(partnerIdToApisSelector);

        const { data, meta } = await Service.Orders.V1.getByCustomerId({
          customerId,
          page,
          limit: 10,
          screenName,
          advisorId: advisorSelected,
          partnerId: partnerSelected,
        });

        if (!data) {
          return { data, meta };
        }

        const normalized: CustomerSentOrdersResponseNormalized[] = data.map(({ order }) => ({
          ...order,
          orderDateNormalized: Utils.Mask.DateFormat(order.orderDate, 'dd/MM/yyyy'),
          requestedAmountNormalized: Utils.Mask.Money(order.requestedAmount),
          marketTypeNormalized: order.marketType?.toLocaleUpperCase() ?? '--',
          customerNormalized: {
            customerNameNormalized: order.customer?.customerName ?? '--',
          },
          accountNormalized: {
            custodyNumberNormalized: mountAccount(
              order.account.pnpAccountNumber,
              order.account.pnpAccountDigit
            ),
          },
          offerNormalized: {
            productDisplayNormalized: order.offer?.productDisplay ?? '--',
            productNameNormalized: order.offer.productName ?? '--',
          },
        }));

        return { data: normalized, meta };
      } catch (error) {
        const backendError = error as BackendError;

        if (backendError.statusCode === 404) {
          return { data: [], meta: blankMetaResponse() };
        }

        throw error;
      }
    },
});

export const customerTransitQuery = selectorFamily<
  | {
      data: ICustomerTransitNormalized;
    }
  | undefined,
  { accountNumber: number }
>({
  key: 'CustomerDetail/CustomerTransit',
  get:
    ({ accountNumber }) =>
    async () => {
      try {
        const response = await Service.Finance.V1.getTransitByAccountNumber({
          accountNumber,
          screenName: 'customerDetails',
        });

        const data = normalizeCustomerTransits(response.data);

        return {
          data,
        };
      } catch (error) {
        const backendError = error as BackendError;

        if (backendError.statusCode === 404) {
          return {
            data: { totalNormalized: 0, operationsNormalized: [] },
            meta: blankMetaResponse(),
          };
        }

        throw error;
      }
    },
});

export const customerBlockedQuery = selectorFamily<
  | {
      data: ICustomerBlockedNormalized;
    }
  | undefined,
  { accountNumber: number }
>({
  key: 'CustomerDetail/CustomerBlocked',
  get:
    ({ accountNumber }) =>
    async () => {
      try {
        const response = await Service.Finance.V1.getBlockedByAccountNumber({
          accountNumber,
          screenName: 'customerDetails',
        });

        const data = normalizeCustomerBlocked(response.data);

        return {
          data,
        };
      } catch (error) {
        const backendError = error as BackendError;

        if (backendError.statusCode === 404) {
          return {
            data: { blockedAssetsNormalized: [] },
            meta: blankMetaResponse(),
          };
        }

        throw error;
      }
    },
});

export const customerContractedProductsQuery = selectorFamily<
  | {
      data: ICustomerContractedProductsNormalized;
    }
  | undefined,
  { accountId: number }
>({
  key: 'CustomerDetail/customerContractedProductsQuery',
  get:
    ({ accountId }) =>
    async () => {
      try {
        const response = await Service.Customer.V1.getAllProductsContracted({
          accountId,
          screenName,
        });

        const data = normalizeCustomerContractedProducts(response.data || []);

        return {
          data,
        };
      } catch (error) {
        const backendError = error as BackendError;

        if (backendError.statusCode === 404) {
          return {
            data: { contractedProductsAssetsNormalized: [] },
          };
        }

        throw error;
      }
    },
});

export const customerGenialLimitQuery = selectorFamily<
  | {
      data: ICustomerGenialLimitNormalized;
    }
  | undefined,
  { accountId: number; accountNumber: number }
>({
  key: 'CustomerDetail/customerGenialLimitQuery',
  get:
    ({ accountId, accountNumber }) =>
    async () => {
      const response = await Service.Finance.V1.getLimitGenialByBalanceAccountIdAccountNumber({
        accountId,
        accountNumber,
        screenName,
      });

      const data = normalizeCustomerGenialLimit(response.data);

      return {
        data,
      };
    },
});

export const customerInvestedAssetsQuery = selectorFamily<
  | {
      data: ICustomerInvestedAssetsNormalized;
    }
  | undefined,
  { accountNumber: number }
>({
  key: 'CustomerDetail/InvestedAssets',
  get:
    ({ accountNumber }) =>
    async ({ get }) => {
      try {
        const selectedDate = get(customersAssetsDateSelected);

        const response = await Service.Finance.V1.getConsolidatedByAccountNumber({
          accountNumber,
          positionDate: selectedDate,
          screenName: 'customerDetails',
        });

        const data = normalizeCustomerAssets(response.data);

        return {
          data,
        };
      } catch (error) {
        const backendError = error as BackendError;

        if (backendError.statusCode === 404) {
          return {
            data: { assetsNormalized: [], totalValueNormalized: 0 },
            meta: blankMetaResponse(),
          };
        }

        throw error;
      }
    },
});

export const updateIsExportingPdfForCustomerAssets = selector({
  key: 'updateIsExportingPdfForCustomerAssets',
  set: ({ set }, newValue) => {
    const isExporting = newValue as boolean;
    return set(isExportingPdfForCustomersAssets, isExporting);
  },
  get: ({ get }) => {
    const item = get(isExportingPdfForCustomersAssets);
    return item;
  },
});

export const updateCustomersAssetsDateSelected = selector({
  key: 'updateCustomersAssetsDateSelected',
  set: ({ set }, newValue) => {
    const isExporting = newValue as Date | undefined;
    return set(customersAssetsDateSelected, isExporting);
  },
  get: ({ get }) => {
    const item = get(customersAssetsDateSelected);
    return item;
  },
});

export const customerBlockingReasonsQuery = selectorFamily<string[], { customerId: number }>({
  key: 'CustomerDetail/customerBlockingReasonsQuery',
  get:
    ({ customerId }) =>
    async () => {
      try {
        const response = await Service.Customer.V2.getPendingRegistrationsByCustomerId({
          customerId,
          screenName,
        });

        const data = normalizeCustomerBlockingReasons(response);

        return data;
      } catch (_error) {
        return [];
      }
    },
});

export const customerStrategyDetailsQuery = selectorFamily<
  StrategyAssetsQueryItem[],
  { accountNumber: number; strategyId: number }
>({
  key: 'CustomerDetail/customerStrategyDetailsQuery',
  get:
    ({ accountNumber, strategyId }) =>
    async () => {
      const response = await Service.Finance.V1.getConsolidatedPositionByByStrategyAccountNumber({
        accountNumber,
        strategyId,
        screenName,
      });

      const data = normalizeCustomerStrategyOrLiquidityDetails(response.data);

      return data;
    },
});

export const customerLiquidityDetailsQuery = selectorFamily<
  LiquidityAssetsQueryItem[],
  { accountNumber: number; liquidityId: number }
>({
  key: 'CustomerDetail/customerLiquidityDetailsQuery',
  get:
    ({ accountNumber, liquidityId }) =>
    async () => {
      const response = await Service.Finance.V1.getConsolidatedPositionByByLiquidityAccountNumber({
        accountNumber,
        liquidityId,
        screenName,
      });

      const data = normalizeCustomerStrategyOrLiquidityDetails(response.data);

      return data;
    },
});

export const customerRecommendedPortfoliosQuery = selectorFamily<
  RecommendedPortfolioResponse,
  { accountNumber: number }
>({
  key: 'CustomerDetail/customerRecommendedPortfoliosQuery',
  get:
    ({ accountNumber }) =>
    async () => {
      const response =
        await Service.Finance.V1.getConsolidatedByEquityAccountNumberRecommendedPortfolios({
          accountNumber,
          screenName,
        });

      const data = normalizeRecommendedPortfolioDetails(response.data);

      return data;
    },
});
